import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Header } from '../components/Header';
import { useToast } from '../components/Toast';
import { Plus, Trash2, GripVertical, Package, Search, X } from 'lucide-react';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { productsService, upsellService } from '../services/firebaseService';

// Composant pour chaque produit upsell draggable
const SortableUpsellItem = ({ item, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.upsell_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  // Images Firebase Storage uniquement (URLs complètes)
  const imageUrl = item.product?.image_url || item.product?.image || null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 bg-white border rounded-xl mb-3 ${isDragging ? 'shadow-xl border-orange-400' : 'border-gray-200 hover:border-orange-300'}`}
    >
      {/* Handle de drag */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 rounded-lg"
      >
        <GripVertical className="w-5 h-5 text-gray-400" />
      </div>

      {/* Image */}
      {imageUrl ? (
        <img src={imageUrl} alt={item.product?.name} className="w-16 h-16 object-cover rounded-lg" />
      ) : (
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
          <Package className="w-8 h-8 text-gray-400" />
        </div>
      )}

      {/* Infos produit */}
      <div className="flex-1">
        <p className="font-bold text-gray-900">{item.product?.name}</p>
        <p className="text-sm text-gray-500">{item.product?.category_name || item.product?.category}</p>
      </div>

      {/* Prix */}
      <p className="text-xl font-black text-primary">{(item.product?.price || item.product?.base_price || 0).toFixed(2)}€</p>

      {/* Bouton supprimer */}
      <button
        onClick={() => onRemove(item.upsell_id)}
        className="p-3 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
      >
        <Trash2 className="w-5 h-5" />
      </button>
    </div>
  );
};

export const UpsellPage = () => {
  const toast = useToast();
  const [upsellProducts, setUpsellProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // FIREBASE DIRECT
      const [upsellRes, productsRes] = await Promise.all([
        upsellService.getAll(),
        productsService.getAll()
      ]);
      // Mapper upsell_id pour compatibilité avec le composant
      const upsells = (upsellRes.upsells || []).map(u => ({
        ...u,
        upsell_id: u.id
      }));
      setUpsellProducts(upsells);
      setAllProducts(productsRes?.products || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur', 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (productId) => {
    try {
      // FIREBASE DIRECT
      await upsellService.add(productId);
      toast.success('Produit ajouté', 'Le produit a été ajouté aux upsells');
      loadData();
      setShowAddModal(false);
      setSearchTerm('');
    } catch (error) {
      toast.error('Erreur', 'Impossible d\'ajouter le produit');
    }
  };

  const handleRemove = async (upsellId) => {
    if (!window.confirm('Retirer ce produit des upsells ?')) return;
    try {
      // FIREBASE DIRECT
      await upsellService.remove(upsellId);
      toast.success('Produit retiré', 'Le produit a été retiré des upsells');
      loadData();
    } catch (error) {
      toast.error('Erreur', 'Impossible de retirer le produit');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = upsellProducts.findIndex(p => p.upsell_id === active.id);
    const newIndex = upsellProducts.findIndex(p => p.upsell_id === over.id);

    const newOrder = arrayMove(upsellProducts, oldIndex, newIndex);
    setUpsellProducts(newOrder);

    // Sauvegarder l'ordre
    setSaving(true);
    try {
      // FIREBASE DIRECT
      await upsellService.updateOrder(newOrder.map((item, index) => ({
        id: item.upsell_id,
        display_order: index
      })));
    } catch (error) {
      toast.error('Erreur', 'Impossible de sauvegarder l\'ordre');
      loadData();
    } finally {
      setSaving(false);
    }
  };

  // Produits disponibles (pas déjà en upsell, actifs, en stock)
  const upsellProductIds = upsellProducts.map(u => u.product_id);
  const availableProducts = allProducts.filter(p =>
    !upsellProductIds.includes(p.id) &&
    p.is_available !== false &&
    !p.is_out_of_stock &&
    p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8">
        <Header title="💰 Ventes Upsell" subtitle="Produits suggérés dans le panier client" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Header title="💰 Ventes Upsell" subtitle="Produits suggérés dans le panier client" />

      {/* Actions */}
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-gray-500">
          {upsellProducts.length} produit(s) configuré(s)
          {saving && <span className="ml-2 text-orange-500">Sauvegarde...</span>}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un produit
        </button>
      </div>

      {/* Info box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
        <p className="text-yellow-800">
          <strong>ℹ️ Comment ça marche :</strong><br />
          • Les produits ci-dessous seront suggérés dans le panier du client<br />
          • Maximum <strong>4 produits</strong> affichés à la fois dans l'app<br />
          • Les produits déjà dans le panier ne sont pas suggérés<br />
          • Glissez-déposez pour réordonner les priorités
        </p>
      </div>

      {/* Liste des produits upsell */}
      {upsellProducts.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">Aucun produit upsell configuré</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-red-700"
          >
            + Ajouter un produit
          </button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={upsellProducts.map(p => p.upsell_id)}
            strategy={verticalListSortingStrategy}
          >
            {upsellProducts.map((item) => (
              <SortableUpsellItem
                key={item.upsell_id}
                item={item}
                onRemove={handleRemove}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Modal Ajouter */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col mx-4">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">Ajouter un produit upsell</h2>
              <button
                onClick={() => { setShowAddModal(false); setSearchTerm(''); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Recherche */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            {/* Liste des produits */}
            <div className="flex-1 overflow-y-auto p-4">
              {availableProducts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {searchTerm ? 'Aucun produit trouvé' : 'Tous les produits sont déjà en upsell'}
                </p>
              ) : (
                availableProducts.slice(0, 30).map(product => {
                  // Images Firebase Storage uniquement (URLs complètes)
                  const imageUrl = product.image_url || product.image || null;

                  return (
                    <div
                      key={product.id}
                      onClick={() => handleAddProduct(product.id)}
                      className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors mb-2"
                    >
                      {imageUrl ? (
                        <img src={imageUrl} alt={product.name} className="w-14 h-14 rounded-lg object-cover" />
                      ) : (
                        <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-500">{product.category_name || product.category}</p>
                      </div>
                      <p className="font-bold text-primary">{(product.base_price || product.price || 0).toFixed(2)}€</p>
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center">
                        <Plus className="w-4 h-4" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpsellPage;
